import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { isEqual, isFunction } from "lodash";

export interface QueryParams {
  [key: string]: string | number | undefined;
}

interface LawAgentsContextProps {
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => void;
}

const LawAgentsContext = createContext<LawAgentsContextProps | undefined>(undefined);

export function useLawAgentsContext(): LawAgentsContextProps {
  const context = useContext(LawAgentsContext);
  if (!context) {
    throw new Error("useLawAgentsContext must be used within a LawAgentsProvider");
  }
  return context;
}

interface LawAgentsProviderProps {
  children: ReactNode;
}

export function LawAgentsProvider({ children }: LawAgentsProviderProps) {
  const history = useHistory();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});

  const setQueryParams = useCallback(
    (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => {
      setQueryParamsBase(prevQueryParams => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }

        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }

        for (let key in nextQueryParams) {
          if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) {
            delete nextQueryParams[key];
          }
        }

        const path = history.location.pathname;
        const newQueryParams = new URLSearchParams(nextQueryParams as Record<string, string>);
        history.replace(`${path}?${newQueryParams.toString()}`);

        return nextQueryParams;
      });
    },
    [history]
  );

  const value: LawAgentsContextProps = {
    queryParams,
    setQueryParamsBase,
    setQueryParams,
  };

  return <LawAgentsContext.Provider value={value}>{children}</LawAgentsContext.Provider>;
}